/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo, useRef } from "react";
import { Search } from "react-feather";
import {
  payoutService,
  BrandPayoutHistory,
} from "../../../services/payout.service";
import { toast } from "react-hot-toast";
import { format } from "date-fns";
import PageHeader from "../../../components/common/PageHeader";
import { Link, useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  DataTable,
  DataTablePagination,
} from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { TableCard } from "@/components/ui/table-card";
import { PayoutAmount } from "@/components/payouts/PayoutAmount";
import { UserAvatar } from "@/components/common/UserAvatar";

export function PayoutHistory() {
  const navigate = useNavigate();
  const [payoutHistory, setPayoutHistory] = useState<BrandPayoutHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState("");

  // Track if we're currently fetching to prevent duplicate calls
  const isFetchingRef = useRef(false);

  useEffect(() => {
    fetchPayoutHistory();
  }, [currentPage, pageSize]);

  const fetchPayoutHistory = async () => {
    // Prevent duplicate calls
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      const response = await payoutService.getBrandPayoutHistory(
        currentPage,
        pageSize
      );
      setPayoutHistory(response.data);
      setTotalPages(response.pagination.totalPages);
      // Prefer the API's real total; fall back to a derived count if absent.
      setTotal(
        response.pagination.total ??
          response.pagination.totalPages * pageSize
      );
    } catch (error) {
      console.error("Failed to fetch payout history:", error);
      toast.error("Failed to load payout history");
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  // Filter the loaded rows on the same fields the payouts page searches
  const filteredHistory = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return payoutHistory;
    return payoutHistory.filter((payout) => {
      const name =
        `${payout.affiliate.firstName} ${payout.affiliate.lastName}`.toLowerCase();
      return (
        name.includes(query) ||
        (payout.code || "").toLowerCase().includes(query) ||
        (payout.affiliate.email || "").toLowerCase().includes(query)
      );
    });
  }, [payoutHistory, searchQuery]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PENDING":
        return "text-muted-foreground bg-muted";
      case "PAID":
        return "text-brand-700 bg-brand-100";
      default:
        return "text-muted-foreground bg-muted";
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns = useMemo<ColumnDef<BrandPayoutHistory, any>[]>(
    () => [
      {
        id: "affiliate",
        header: "Affiliate",
        cell: ({ row }) => {
          const payout = row.original;
          return (
            <div className="flex items-center gap-3 min-w-0">
              <UserAvatar
                src={payout.affiliate.profileImage}
                name={`${payout.affiliate.firstName} ${payout.affiliate.lastName}`}
                className="w-8 h-8 shrink-0 text-xs"
              />
              <Link
                to={`/brand/affiliates/${payout.affiliateId}`}
                className="break-words font-medium text-foreground hover:underline"
              >
                {payout.affiliate.firstName} {payout.affiliate.lastName}
              </Link>
            </div>
          );
        },
        meta: {
          width: "w-48",
          skeleton: (
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
              <div className="h-3.5 w-28 rounded bg-muted animate-pulse" />
            </div>
          ),
        },
      },
      {
        id: "email",
        header: "Email",
        cell: ({ row }) => (
          <span className="block break-all text-muted-foreground">
            {row.original.affiliate.email}
          </span>
        ),
        meta: { width: "w-56" },
      },
      {
        id: "amount",
        header: "Amount",
        cell: ({ row }) => (
          <PayoutAmount
            amount={row.original.amount}
            breakdown={row.original.breakdown}
            className="font-medium"
          />
        ),
        meta: { width: "w-24" },
      },
      {
        id: "requested",
        header: "Date Requested",
        cell: ({ row }) =>
          format(new Date(row.original.createdAt), "MM/dd/yyyy"),
        meta: { width: "w-32" },
      },
      {
        id: "paid",
        header: "Date Paid",
        cell: ({ row }) =>
          row.original.status === "PAID"
            ? format(new Date(row.original.updatedAt), "MM/dd/yyyy")
            : "---",
        meta: { width: "w-32" },
      },
      {
        id: "method",
        header: "Payment Method",
        cell: ({ row }) => {
          const payout = row.original;
          return payout.paymentDetails &&
            payout.paymentDetails.paymentMethodName ? (
            <span className="capitalize">
              {payout.paymentDetails.paymentMethodName.toLowerCase()}
            </span>
          ) : payout.matchingPaymentInfos &&
            payout.matchingPaymentInfos.length > 0 ? (
            <span className="capitalize">
              {payout.matchingPaymentInfos[0].paymentMethod.name.toLowerCase()}
            </span>
          ) : (
            <span>---</span>
          );
        },
        meta: { width: "w-36" },
      },
      {
        id: "methodInfo",
        header: "Payment Method Info",
        cell: ({ row }) => {
          const payout = row.original;
          return (
            <span className="block break-all">
              {payout.paymentDetails && payout.paymentDetails.fieldData
                ? Object.values(payout.paymentDetails.fieldData)[0] || "---"
                : payout.matchingPaymentInfos &&
                  payout.matchingPaymentInfos.length > 0
                ? Object.values(
                    payout.matchingPaymentInfos[0].fieldData
                  )[0] || "---"
                : payout.affiliate?.email || "---"}
            </span>
          );
        },
        meta: { width: "w-56" },
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => (
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
              row.original.status
            )}`}
          >
            {row.original.status === "PAID" ? "Paid" : "Pending"}
          </span>
        ),
        meta: {
          width: "w-24",
          skeleton: <div className="h-5 w-16 rounded-full bg-muted animate-pulse" />,
        },
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payout history"
        showDefaultActions={false}
        customActions={[
          {
            id: "payouts",
            label: "View Payouts",
            onClick: () => navigate("/brand/payouts"),
          },
        ]}
      />

      {/* Filters and Search */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex flex-wrap items-center gap-2 md:ml-auto">
          <div className="relative flex-1 min-w-[200px]">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-muted-foreground" />
            </div>
            <Input
              type="text"
              className="pl-10"
              placeholder="Search by name, code, or email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <TableCard>
        <DataTable
          columns={columns}
          data={filteredHistory}
          isLoading={isLoading}
          emptyText="No payout history available"
          getRowId={(row) => row.id}
          onRowClick={(row) =>
            navigate(`/brand/payouts/history/${row.id}`, {
              state: { payout: row },
            })
          }
        />
        <DataTablePagination
          page={currentPage}
          totalPages={totalPages}
          total={total}
          pageSize={pageSize}
          onPageChange={(page) => {
            setCurrentPage(page);
            window.scrollTo(0, 0);
          }}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          noun="payouts"
        />
      </TableCard>
    </div>
  );
}
